/**
 * The row five departments share (WALLIYO-0132).
 *
 * Trust, Finance, Product, Growth and Administration all draw the same row in
 * their approved designs: a bar carrying the urgency, a line naming the thing, a
 * line saying why it is urgent, and the way out. What differs is what fills it,
 * so the shape lives here once and each department passes its own words.
 *
 * Both sentences are the console's, at `overview.<department>.row.<rowId>` and
 * `.why` — which words are true depends on the kind of row, and the server sends
 * the kind with the names and numbers those words need.
 */

import type { QueueRow } from "@wallyo/contracts/console/overview";

import { t, tCount, type MessageId } from "../../i18n";
import { formatDate, formatNumber, formatTime } from "../../i18n/format";

export function QueueRows({
  rows,
  area,
  empty,
}: {
  rows: readonly QueueRow[];
  /** The department the words belong to: `trust`, `finance`, … */
  area: string;
  empty: string;
}) {
  if (rows.length === 0) return <p className="foot">{empty}</p>;
  return (
    <>
      {rows.map((row, index) => (
        <Line key={`${row.rowId}-${row.reference ?? index}`} row={row} area={area} />
      ))}
    </>
  );
}

function Line({ row, area }: { row: QueueRow; area: string }) {
  const words = {
    subject: row.subject,
    reference: row.reference ?? "",
    amount: row.amount ?? "",
    days: formatNumber(row.days ?? 0),
    on: row.on ? formatDate(row.on, { day: "numeric", month: "long" }) : "",
    // The same instant said the other way: "Monday 06:00". A scheduled rule
    // lands at a time, and a date alone loses the half that matters.
    at: row.on
      ? row.on.includes("T")
        ? `${formatDate(row.on, { weekday: "long" })} ${formatTime(row.on)}`
        : formatDate(row.on, { weekday: "long" })
      : "",
  };
  return (
    <div className="ov-row">
      <span className={`ov-bar-side ${row.tone}`} aria-hidden="true" />
      <span className="grow">
        <span className="ov-row-what">
          {tCount(`overview.${area}.row.${row.rowId}` as MessageId, row.days ?? 0, words)}
        </span>
        <span
          className={
            row.tone === "bad" ? "ov-row-why danger" : row.tone === "warn" ? "ov-row-why warn-text" : "ov-row-why"
          }
        >
          {row.reference ? <span className="mono ov-ref">{row.reference} · </span> : null}
          {t(`overview.${area}.why.${row.rowId}` as MessageId, words)}
        </span>
      </span>
      <a className={row.tone === "bad" ? "btn primary" : "btn"} href={row.href}>
        {t(`overview.action.${row.actionId}` as MessageId)}
      </a>
    </div>
  );
}

/** A small panel that is a list of two or three named counts. */
export function Tallies({
  tallies,
  area,
}: {
  tallies: readonly { tallyId: string; count: number; tone?: string }[];
  area: string;
}) {
  return (
    <>
      {tallies.map((tally) => (
        <div key={tally.tallyId} className="ov-line">
          <span className="grow">{t(`overview.${area}.tally.${tally.tallyId}` as MessageId)}</span>
          <span className={tally.tone === "warn" ? "mono warn-text" : tally.tone === "bad" ? "mono danger" : "mono"}>
            {formatNumber(tally.count)}
          </span>
        </div>
      ))}
    </>
  );
}
